﻿import { FC, useState } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, CardFooter, Button, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { getPaidProducts, formatPriceINR } from '../config/catalog';
import {
  trackConsultationClick,
  trackProductView,
  trackBuyClick,
  trackLeadMagnetView
} from '../analytics/events';

export interface PageProps {
  onNavigate: (path: string) => void;
}

export const HomePage: FC<PageProps> = ({ onNavigate }) => {
  const paidProducts = getPaidProducts();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (idx: number) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const handleConsultationClick = (section: string) => {
    trackConsultationClick('free_consultation', section);
    onNavigate(ROUTES.CONTACT);
  };

  const handleProductClick = (slug: string, price: number, path: string) => {
    trackProductView(slug, price, 'INR');
    trackBuyClick(slug, price);
    onNavigate(path);
  };

  const handleLeadMagnetClick = () => {
    trackLeadMagnetView('free-interview-guide', 'Interview Preparation Starter Guide');
    onNavigate(ROUTES.FREE_INTERVIEW_GUIDE);
  };

  const careerSteps = [
    {
      step: '01',
      title: 'Career Clarity',
      description: 'Define your core strengths, values, and realistic target roles through structured discovery.'
    },
    {
      step: '02',
      title: 'Skill & Gap Analysis',
      description: 'Audit your current market positioning against job descriptions to identify competitive gaps.'
    },
    {
      step: '03',
      title: 'Resume & LinkedIn Positioning',
      description: 'Rebuild your professional narrative with ATS-compliant formatting and recruiter-targeted keywords.'
    },
    {
      step: '04',
      title: 'Interview Preparation',
      description: 'Master behavioral, technical, and executive interviews with structured STAR response frameworks.'
    },
    {
      step: '05',
      title: 'Job Search Strategy',
      description: 'Deploy direct outreach, recruiter networking, and hidden job market navigation techniques.'
    },
    {
      step: '06',
      title: 'Career Growth',
      description: 'Evaluate offers, negotiate total compensation packages, and plan your long-term progression.'
    }
  ];

  const businessServices = [
    {
      title: 'Recruitment & Talent Acquisition',
      desc: 'End-to-end hiring workflows, candidate screening scorecards, and specialized talent sourcing for critical startup and SME roles.'
    },
    {
      title: 'HR Operations & Support',
      desc: 'Employment policy documentation, structured onboarding checklists, statutory guidance, and day-to-day HR operational assistance.'
    },
    {
      title: 'Virtual Assistance',
      desc: 'Dedicated remote administrative, research, and scheduling support to keep executive leadership focused on core business priorities.'
    },
    {
      title: 'Lead Generation',
      desc: 'B2B prospect list curation, ICP validation, and multi-channel pipeline development tailored to your target industry.'
    },
    {
      title: 'LinkedIn Outreach',
      desc: 'Strategic executive profile management, personalized connection cadences, and professional relationship building.'
    },
    {
      title: 'Digital Marketing Support',
      desc: 'Content scheduling, professional brand positioning, campaign execution, and audience engagement coordination.'
    }
  ];

  const processSteps = [
    { number: '1', title: 'Understand Your Goal', desc: 'We start by learning your exact career aspiration or organizational operational needs.' },
    { number: '2', title: 'Identify the Challenge', desc: 'Pinpoint specific friction points — whether resume rejection or recruitment bottlenecks.' },
    { number: '3', title: 'Build the Strategy', desc: 'Craft a step-by-step roadmap with tangible deliverables, timelines, and milestones.' },
    { number: '4', title: 'Take Action', desc: 'Execute the plan with disciplined support, tailored frameworks, and direct feedback.' },
    { number: '5', title: 'Track Progress', desc: 'Review metrics, interview callbacks, or candidate pipeline velocity to ensure alignment.' }
  ];

  const faqs = [
    {
      q: 'Who is career coaching for?',
      a: 'Our career coaching programs are designed for students, freshers, early career job seekers, mid-level professionals aiming for leadership promotions, and experienced individuals planning a strategic industry switch.'
    },
    {
      q: 'What does career coaching include?',
      a: 'Depending on the program selected (Starter Clarity, Pro Accelerator, or Premium Transformation), coaching covers 1-on-1 sessions, ATS resume rewriting, LinkedIn optimization, interview simulation with structured feedback, offer negotiation, and targeted job search strategies.'
    },
    {
      q: 'Who are the digital products for?',
      a: 'Our digital products are self-paced, actionable toolkits created for professionals who want immediate, ready-to-use resume templates, LinkedIn frameworks, interview answer cheat sheets, and AI job search prompts.'
    },
    {
      q: 'Are digital products delivered immediately?',
      a: 'Yes. Upon successful checkout verification, digital products are made available immediately for download and backed by a secure delivery token sent to your email.'
    },
    {
      q: 'Can international customers purchase?',
      a: 'Currently, catalog pricing is configured in Indian Rupees (INR) for domestic transactions. International currency pricing will be rolled out in upcoming phases.'
    },
    {
      q: 'Can companies hire Connect Source for HR support?',
      a: 'Yes. Connect Source provides flexible B2B business solutions including recruitment, talent acquisition scorecards, HR operations support, and executive virtual assistance for startups, entrepreneurs, and SMEs.'
    },
    {
      q: 'How do consultations work?',
      a: 'You can request a free initial consultation via our contact form. We will coordinate a suitable time to understand your specific requirements and recommend the most effective pathway.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Transform Your Career. Strengthen Your Business."
        description="Personalized career coaching for professionals and practical HR & business support for growing companies. Actionable digital toolkits, ATS resumes, and executive advisory."
      />

      {/* 1. HERO SECTION */}
      <section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '5rem 0 4.5rem 0' }}>
        <Container size="xl">
          <div style={{ maxWidth: '820px', margin: '0 auto', textAlign: 'center' }}>
            <Badge variant="primary" style={{ marginBottom: '1.25rem', padding: '0.4rem 0.85rem' }}>
              Career Transformation & HR Advisory
            </Badge>
            <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.25rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.18, letterSpacing: '-0.02em' }}>
              Transform Your Career. Strengthen Your Business.
            </h1>
            <p style={{ fontSize: '1.2rem', color: 'var(--color-text-secondary)', marginTop: '1.5rem', lineHeight: 1.65 }}>
              Personalized career coaching for professionals and practical HR & business support for growing companies.
            </p>
            <div style={{ marginTop: '2.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleConsultationClick('hero_section')}
              >
                Book a Free Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => onNavigate(ROUTES.CAREER_COACHING)}
              >
                Explore Career Coaching
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* 2. CAREER JOURNEY */}
      <Section
        title="The Career Transformation Journey"
        subtitle="A structured, 6-stage methodology designed to build long-term career momentum."
      >
        <div className="cs-grid-3">
          {careerSteps.map(item => (
            <Card key={item.step} className="cs-card-hover" style={{ display: 'flex', flexDirection: 'column', padding: '1.75rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-brand-primary)', marginBottom: '0.75rem' }}>
                {item.step}
              </div>
              <CardTitle style={{ fontSize: '1.2rem' }}>{item.title}</CardTitle>
              <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6, fontSize: '0.925rem' }}>
                {item.description}
              </CardDescription>
            </Card>
          ))}
        </div>
      </Section>

      {/* 3. DIGITAL CAREER TOOLKIT */}
      <Section
        title="Practical Career Tools You Can Use Today"
        subtitle="Engineered frameworks, templates, and actionable guides built to improve your callback rates."
        style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--color-border-subtle)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div className="cs-grid-3">
          {paidProducts.map(product => (
            <Card key={product.id} className="cs-card-hover" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '1.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <Badge variant="neutral">{product.deliverableType.replace('_', ' ')}</Badge>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                  {formatPriceINR(product.priceINR)}
                </div>
              </div>

              <CardTitle style={{ fontSize: '1.2rem', minHeight: '3rem' }}>{product.title}</CardTitle>
              <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6, flex: '1 0 auto' }}>
                {product.subtitle}
              </CardDescription>

              <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border-subtle)' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-navy-dark)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                  Target Audience:
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                  {product.targetAudience.join(', ')}
                </div>
              </div>

              <CardFooter style={{ marginTop: '1.25rem', paddingTop: '1rem' }}>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => handleProductClick(product.slug, product.priceINR, product.routePath)}
                  style={{ width: '100%' }}
                >
                  View Toolkit Details
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </Section>

      {/* 4. FREE LEAD MAGNET */}
      <section style={{ backgroundColor: 'var(--color-navy-dark)', color: '#ffffff', padding: '4.5rem 0' }}>
        <Container size="lg">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2.5rem', alignItems: 'center' }}>
            <div>
              <Badge variant="teal" style={{ marginBottom: '1rem' }}>Complimentary Resource</Badge>
              <h2 style={{ fontSize: '2.25rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.25 }}>
                Get Your Free Interview Preparation Guide
              </h2>
              <p style={{ fontSize: '1.05rem', color: '#cbd5e1', marginTop: '1rem', lineHeight: 1.65 }}>
                Download our 10-point pre-interview readiness checklist, proven STAR response framework, and actionable guidance to help you stand out. Completely free.
              </p>
              <div style={{ marginTop: '1.25rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                Note: This free starter resource is an independent guide distinct from our full Interview Cracker Kit.
              </div>
            </div>
            <div style={{ textAlign: 'center', backgroundColor: 'var(--color-navy-surface)', padding: '2.5rem 2rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--color-navy-border)' }}>
              <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#ffffff', marginBottom: '0.75rem' }}>
                Immediate Free Access
              </div>
              <p style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '1.75rem' }}>
                No credit card required. Receive your personal download link directly.
              </p>
              <Button
                variant="teal"
                size="lg"
                onClick={handleLeadMagnetClick}
                style={{ width: '100%' }}
              >
                Get the Free Guide →
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* 5. BUSINESS SOLUTIONS */}
      <Section
        title="Practical HR & Business Support for Growing Companies"
        subtitle="Specialized business capabilities engineered for startups, entrepreneurs, and expanding enterprises."
      >
        <div className="cs-grid-3">
          {businessServices.map((svc, i) => (
            <Card key={i} className="cs-card-hover" style={{ padding: '1.75rem' }}>
              <CardTitle style={{ fontSize: '1.15rem', color: 'var(--color-navy-dark)' }}>{svc.title}</CardTitle>
              <CardDescription style={{ marginTop: '0.75rem', lineHeight: 1.65, fontSize: '0.925rem' }}>
                {svc.desc}
              </CardDescription>
            </Card>
          ))}
        </div>
        <div style={{ marginTop: '2.5rem', textAlign: 'center' }}>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => onNavigate(ROUTES.BUSINESS_SOLUTIONS)}
          >
            Explore Business Solutions
          </Button>
        </div>
      </Section>

      {/* 6. HOW IT WORKS */}
      <Section
        title="How It Works"
        subtitle="A clear, collaborative process to guarantee transparency and measurable outcomes."
        style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--color-border-subtle)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          {processSteps.map(step => (
            <div key={step.number} style={{ padding: '1.5rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-subtle)' }}>
              <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', backgroundColor: 'var(--color-brand-primary)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem', marginBottom: '1rem' }}>
                {step.number}
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>{step.title}</h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.55 }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* 7. WHY CONNECT SOURCE */}
      <Section
        title="Why Connect Source"
        subtitle="We focus on practical, personalized, and structured execution — zero fluff, zero false promises."
      >
        <div className="cs-grid-3">
          <Card style={{ padding: '1.75rem' }}>
            <CardTitle style={{ fontSize: '1.15rem' }}>Tailored & Personalized</CardTitle>
            <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
              No generic templates or cookie-cutter advice. Every coaching session and business consulting engagement is built around your individual goals.
            </CardDescription>
          </Card>
          <Card style={{ padding: '1.75rem' }}>
            <CardTitle style={{ fontSize: '1.15rem' }}>Practical & Field-Tested</CardTitle>
            <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
              Our tools and recommendations reflect real recruiter expectations, modern ATS algorithms, and hands-on hiring experience.
            </CardDescription>
          </Card>
          <Card style={{ padding: '1.75rem' }}>
            <CardTitle style={{ fontSize: '1.15rem' }}>Transparent & Ethical</CardTitle>
            <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
              We believe in honest career advisory. We do not invent statistics, make unrealistic salary guarantees, or lock clients into rigid contracts.
            </CardDescription>
          </Card>
        </div>
      </Section>

      {/* 8. ABOUT NILESH */}
      <section style={{ backgroundColor: '#ffffff', padding: '4rem 0', borderTop: '1px solid var(--color-border-subtle)', borderBottom: '1px solid var(--color-border-subtle)' }}>
        <Container size="lg">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '3rem', alignItems: 'center' }}>
            <div>
              <div style={{ width: '5.5rem', height: '5.5rem', borderRadius: 'var(--radius-xl)', backgroundColor: 'var(--color-navy-dark)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', fontWeight: 800, marginBottom: '1.25rem' }}>
                NM
              </div>
              <Badge variant="primary">Leadership</Badge>
              <h2 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-navy-dark)', marginTop: '0.75rem' }}>
                Nilesh Mishra
              </h2>
              <div style={{ fontSize: '1.05rem', color: 'var(--color-brand-primary)', fontWeight: 600, marginTop: '0.25rem' }}>
                Founder — Connect Source
              </div>
            </div>
            <div>
              <p style={{ fontSize: '1.05rem', color: 'var(--color-text-secondary)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                Nilesh Mishra is a dedicated Career Transformation Coach, Certified Human Resource Professional (CHRM), and Author of <em>"The Balanced Empire"</em>.
              </p>
              <p style={{ fontSize: '0.95rem', color: 'var(--color-text-secondary)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                With a deep focus on bridging the gap between professional ambitions and organizational hiring standards, Nilesh founded Connect Source to provide grounded, structured mentorship for job seekers alongside agile HR support for scaling companies.
              </p>
              <Button variant="outline" onClick={() => onNavigate(ROUTES.ABOUT)}>
                Read Founder Story & Background →
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* 9. TESTIMONIALS (Tasteful Placeholder per instructions) */}
      <Section title="Client Stories & Experiences" subtitle="Real reflections on career transitions and operational milestones.">
        <div style={{ backgroundColor: '#ffffff', padding: '3.5rem 2rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-subtle)', textAlign: 'center', maxWidth: '640px', margin: '0 auto' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>💬</div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-navy-dark)' }}>Client Stories Coming Soon</h3>
          <p style={{ fontSize: '0.925rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem', lineHeight: 1.6 }}>
            We maintain strict integrity and never publish fabricated reviews or synthetic claims. Verified client reflections and corporate case studies are currently being curated for publication.
          </p>
        </div>
      </Section>

      {/* 10. FAQ */}
      <Section
        title="Frequently Asked Questions"
        subtitle="Common questions regarding our coaching programs, business services, and digital toolkits."
        style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--color-border-subtle)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {faqs.map((item, idx) => (
            <div key={idx} className="cs-faq-item">
              <button
                type="button"
                className="cs-faq-question"
                onClick={() => toggleFaq(idx)}
                aria-expanded={openFaq === idx}
              >
                <span>{item.q}</span>
                <span style={{ fontSize: '1.25rem', color: 'var(--color-brand-primary)' }}>
                  {openFaq === idx ? '−' : '+'}
                </span>
              </button>
              {openFaq === idx && (
                <div className="cs-faq-answer">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* 11. FINAL CTA SECTION */}
      <section style={{ padding: '5rem 0', backgroundColor: 'var(--color-bg-canvas)' }}>
        <Container size="lg">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem' }}>
            {/* Career CTA Card */}
            <Card style={{ padding: '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <Badge variant="primary" style={{ marginBottom: '1rem' }}>For Professionals & Job Seekers</Badge>
                <h3 style={{ fontSize: '1.65rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                  Ready to Move Your Career Forward?
                </h3>
                <p style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', marginTop: '0.75rem', lineHeight: 1.6 }}>
                  Gain clarity, upgrade your positioning, and prepare for high-impact interviews with personalized coaching.
                </p>
              </div>
              <div style={{ marginTop: '2rem' }}>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => handleConsultationClick('final_career_cta')}
                  style={{ width: '100%' }}
                >
                  Schedule Career Consultation
                </Button>
              </div>
            </Card>

            {/* Business CTA Card */}
            <Card style={{ padding: '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: 'var(--color-navy-surface)', color: '#ffffff', border: 'none' }}>
              <div>
                <Badge variant="teal" style={{ marginBottom: '1rem' }}>For Companies & Founders</Badge>
                <h3 style={{ fontSize: '1.65rem', fontWeight: 800, color: '#ffffff' }}>
                  Need Practical HR & Business Support?
                </h3>
                <p style={{ fontSize: '1rem', color: '#cbd5e1', marginTop: '0.75rem', lineHeight: 1.6 }}>
                  Strengthen your recruitment pipeline, streamline HR operations, and scale with dedicated support.
                </p>
              </div>
              <div style={{ marginTop: '2rem' }}>
                <Button
                  variant="teal"
                  size="lg"
                  onClick={() => handleConsultationClick('final_business_cta')}
                  style={{ width: '100%' }}
                >
                  Inquire for Business Support
                </Button>
              </div>
            </Card>
          </div>
        </Container>
      </section>
    </>
  );
};


